import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { combineLatest, merge, Observable } from 'rxjs';
import { map, shareReplay, startWith, switchMap, take, withLatestFrom } from 'rxjs/operators';

import {
    AdjustItemQuantityMutation, AdjustItemQuantityMutationVariables,
    GetActiveOrderQuery,
    RemoveItemFromCartMutation, RemoveItemFromCartMutationVariables
} from '../../../common/generated-types';
import { DataService } from '../../providers/data/data.service';
import { NotificationService } from '../../providers/notification/notification.service';
import { StateService } from '../../providers/state/state.service';

import { Router } from '@angular/router';
import { CartDrawerComponentImports } from '../..';
import { CustomerService } from '../../../core/providers/customer/customer.service';
import { ActiveService } from '../../providers/active/active.service';
import { CartFacade } from '../../providers/cart/cart.facade';
import { ADJUST_ITEM_QUANTITY, REMOVE_ITEM_FROM_CART } from './cart-drawer.graphql';

@Component({
    standalone: true,
    imports: CartDrawerComponentImports,
    selector: 'vsf-cart-drawer',
    templateUrl: './cart-drawer.component.html',
    styleUrls: ['./cart-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartDrawerComponent implements OnInit {
    private dataService = inject(DataService);
    private stateService = inject(StateService);
    private activeService = inject(ActiveService);
    private notificationService = inject(NotificationService);
    private router = inject(Router);
    private cartFacade = inject(CartFacade);
    private customerService = inject(CustomerService);

    @Input() visible = false;
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() close = new EventEmitter<void>();
    @ViewChild('overlay') private overlayRef: ElementRef<HTMLDivElement>;

    cart$: Observable<GetActiveOrderQuery['activeOrder'] | null>;
    isEmpty$: Observable<boolean>;
    isCompany$: Observable<boolean>;
    productVariantIds$: Observable<string[]>;
    initialized = false;
    taxLabel$: Observable<string>;
    summary$: Observable<{
        originalSubtotalWithTax: number;
        discountAmount: number;
        discountPercent: number;
        taxAmount: number;
        taxPercent: number;
        totalWithTax: number;
        shippingWithTax: number;
    }>;

    ngOnInit() {

        this.cart$ = merge(
            this.stateService.select(state => state.activeOrderId),
            this.stateService.select(state => state.signedIn),
        ).pipe(
            switchMap(() => this.activeService.activeOrder$),
            shareReplay(1),
        );

        this.cart$.pipe(take(1)).subscribe(() => {
            this.initialized = true;
        });

        this.isEmpty$ = this.cart$.pipe(
            map(cart => !cart || cart.lines.length === 0),
        );

        this.isCompany$ = this.customerService.isCompany$;

        this.summary$ = combineLatest([this.cart$, this.isCompany$]).pipe(
            map(([cart, isCompany]) => {
                if (!cart) {
                    return {
                        originalSubtotalWithTax: 0,
                        discountAmount: 0,
                        discountPercent: 0,
                        taxAmount: 0,
                        taxPercent: 0,
                        totalWithTax: 0,
                        shippingWithTax: 0,
                    };
                }

                const discountAmount = Math.abs(
                    cart.discounts?.reduce((sum, d) => sum + d.amountWithTax, 0) ?? 0
                );

                const originalSubtotalWithTax = isCompany
                    ? cart.subTotal + discountAmount
                    : cart.subTotalWithTax + discountAmount;

                const taxAmount = cart.subTotalWithTax - cart.subTotal;

                const discountPercent = originalSubtotalWithTax
                    ? Math.round((discountAmount / originalSubtotalWithTax) * 100)
                    : 0;

                const taxPercent = cart.total
                    ? Math.round((taxAmount / cart.subTotal) * 100)
                    : 0;

                return {
                    originalSubtotalWithTax,
                    discountAmount,
                    discountPercent,
                    taxAmount,
                    taxPercent,
                    totalWithTax: cart.totalWithTax,
                    shippingWithTax: cart.shippingWithTax,
                };
            })
        );

        this.taxLabel$ = this.summary$.pipe(
            withLatestFrom(this.isCompany$),
            map(([summary, isCompany]) => {
                
                return !isCompany
                    ? `inkl. ${summary.taxPercent}% MwSt:`
                    : `zzgl. ${summary.taxPercent}% MwSt:`;
            })
        );
    }

    setQuantity(event: { itemId: string; quantity: number; }) {
        if (0 < event.quantity) {
            this.adjustItemQuantity(event.itemId, event.quantity);
        } else {
            this.removeItem(event.itemId);
        }
    }

    overlayClick(event: MouseEvent) {
        if (event.target === this.overlayRef.nativeElement) {
            this.close.emit();
        }
    }

    private adjustItemQuantity(id: string, qty: number) {
        this.dataService.mutate<AdjustItemQuantityMutation, AdjustItemQuantityMutationVariables>(ADJUST_ITEM_QUANTITY, {
            id,
            qty,
        }).pipe(
            this.activeService.reconcileOnError<AdjustItemQuantityMutation>(result => result.adjustOrderLine),
            take(1),
        ).subscribe(({ adjustOrderLine }) => {
            switch (adjustOrderLine.__typename) {
                case 'Order':
                    this.activeService.setActiveOrder(adjustOrderLine);
                    break;
                case 'InsufficientStockError':
                case 'NegativeQuantityError':
                case 'OrderLimitError':
                case 'OrderModificationError':
                    this.notificationService.error("Unbekannter Fehler (160)").subscribe();
                    break;
            }
        });
    }

    private removeItem(id: string) {
        this.dataService.mutate<RemoveItemFromCartMutation, RemoveItemFromCartMutationVariables>(REMOVE_ITEM_FROM_CART, {
            id,
        }).pipe(
            this.activeService.reconcileOnError<RemoveItemFromCartMutation>(result => result.removeOrderLine),
            take(1),
        ).subscribe(({ removeOrderLine }) => {
            if (removeOrderLine.__typename === 'Order') {
                this.activeService.setActiveOrder(removeOrderLine);
            }
        });
    }

    continuePurchasing() {
        this.cartFacade.continuePurchasing().subscribe({
            error: err => console.error(err),
        });
    }

    public isOrderBlocked$: Observable<boolean> = this.activeService.activeOrder$.pipe(
        map(order => {
            if (!order) return false;
            return order.state === 'ArrangingPayment';
        }),
        startWith(false)
    );
}